import React, { useCallback, useEffect, useState } from 'react';
import type { RecurringDto, QuoteItemKind } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { euro, euroInput, parseEuro } from '../format';
import { Empty, ErrorBanner, Field, Modal, More, Panel } from '../ui';
import { DateInput } from '../pickers';
import { ask } from '../dialogs';

const FREQ = { monthly: 'Ogni mese', quarterly: 'Ogni trimestre', yearly: 'Ogni anno' } as const;
type Freq = keyof typeof FREQ;

const dateIt = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const num = (v: string) => {
  const n = Number(v.trim().replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
const totalOf = (r: RecurringDto) => {
  const sub = r.data.items.reduce((n, i) => n + Math.round(i.quantity * i.unitCents), 0);
  return sub + Math.round((sub * r.data.vatPct) / 100) + r.data.stampCents;
};

interface ItemDraft {
  description: string;
  kind: QuoteItemKind;
  quantity: string;
  unit: string;
}

/** Fatture ricorrenti: canoni e servizi periodici. A ogni scadenza si crea una bozza pronta da emettere. */
export function RecurringPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [list, setList] = useState<RecurringDto[] | null>(null);
  const [editing, setEditing] = useState<RecurringDto | 'new' | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await api.get<RecurringDto[]>('/api/recurring'));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (r: RecurringDto) => {
    if (!(await ask(`Creare ora la bozza di fattura per "${r.name}"? La prossima data slitta al periodo successivo.`))) return;
    try {
      const res = await api.post<{ invoiceId: number | null }>(`/api/recurring/${r.id}/run`);
      if (res.invoiceId) {
        setInfo('Bozza creata: la trovi in Fatture, pronta da controllare ed emettere.');
        ctx.setPage('invoices', { open: res.invoiceId });
        return;
      }
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const toggle = async (r: RecurringDto) => {
    try {
      await api.put(`/api/recurring/${r.id}`, { name: r.name, clientId: r.clientId, frequency: r.frequency, nextDate: r.nextDate, dueDays: r.dueDays, active: !r.active, data: r.data });
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="panels single">
      <Panel
        title="Ricorrenti"
        subtitle="Fatture periodiche: canoni, assistenza, abbonamenti"
        right={
          <button type="button" className="btn dark" title="Imposta una fattura che si ripete: canone, assistenza, abbonamento" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} /> Nuova
          </button>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {info && (
          <div className="note good" style={{ marginBottom: 12 }} onClick={() => setInfo(null)}>
            {info}
          </div>
        )}
        <p className="muted small">Alla data prevista prepara una <b>bozza</b>: non parte niente da solo, la controlli tu.</p>
        {!list ? (
          <div className="muted small">Caricamento…</div>
        ) : list.length === 0 ? (
          <Empty icon="refresh">Nessuna fattura ricorrente.</Empty>
        ) : (
          <div className="ag-list">
            {list.map((r) => (
              <div key={r.id} className={`pay-card${r.active ? '' : ' off'}`}>
                <div className="row-actions" style={{ justifyContent: 'space-between', margin: 0 }}>
                  <b>{r.name}</b>
                  <b>{euro(totalOf(r))}</b>
                </div>
                <div className="muted small">
                  {r.clientName ?? r.data.recipientName} · {FREQ[r.frequency]} · prossima {dateIt(r.nextDate)}
                  {!r.active && ' · sospesa'}
                </div>
                <div className="row-actions" style={{ margin: 0 }}>
                  <button type="button" className="btn small" onClick={() => setEditing(r)}>
                    <Icon name="edit" size={13} /> Modifica
                  </button>
                  <button type="button" className="btn small" title="Prepara subito la bozza di questo periodo" onClick={() => void run(r)}>
                    <Icon name="play" size={13} /> Crea bozza ora
                  </button>
                  <button type="button" className="btn small ghost" title={r.active ? 'Smette di preparare bozze finché non la riattivi' : 'Ricomincia a preparare le bozze'} onClick={() => void toggle(r)}>
                    {r.active ? 'Sospendi' : 'Riattiva'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
      {editing && (
        <RecurringModal
          ctx={ctx}
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function RecurringModal({ ctx, item, onClose, onSaved }: { ctx: Ctx; item: RecurringDto | null; onClose: () => void; onSaved: () => void }) {
  const { api } = ctx;
  const d = item?.data;
  const [clients, setClients] = useState<{ id: number; name: string }[]>([]);
  const [clientId, setClientId] = useState<number | null>(item?.clientId ?? null);
  const [name, setName] = useState(item?.name ?? '');
  const [freq, setFreq] = useState<Freq>(item?.frequency ?? 'monthly');
  const [next, setNext] = useState(item?.nextDate ?? todayKey());
  const [dueDays, setDueDays] = useState(String(item?.dueDays ?? 30));
  const [rName, setRName] = useState(d?.recipientName ?? '');
  const [vat, setVat] = useState(d?.recipientVat ?? '');
  const [cf, setCf] = useState(d?.recipientCf ?? '');
  const [address, setAddress] = useState(d?.recipientAddress ?? '');
  const [cap, setCap] = useState(d?.recipientCap ?? '');
  const [city, setCity] = useState(d?.recipientCity ?? '');
  const [prov, setProv] = useState(d?.recipientProvince ?? '');
  const [email, setEmail] = useState(d?.recipientEmail ?? '');
  const [pec, setPec] = useState(d?.recipientPec ?? '');
  const [sdi, setSdi] = useState(d?.recipientSdi ?? '0000000');
  const [subject, setSubject] = useState(d?.subject ?? '');
  const [notes, setNotes] = useState(d?.notes ?? '');
  const [vatPct, setVatPct] = useState(d ? String(d.vatPct).replace('.', ',') : '22');
  const [stamp, setStamp] = useState((d?.stampCents ?? 0) > 0);
  const [items, setItems] = useState<ItemDraft[]>(
    d ? d.items.map((i) => ({ description: i.description, kind: i.kind, quantity: String(i.quantity).replace('.', ','), unit: euroInput(i.unitCents) })) : [{ description: '', kind: 'fixed', quantity: '1', unit: '' }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ id: number; name: string }[]>('/api/invoices/meta/clients').then(setClients).catch(() => undefined);
  }, [api]);

  const setItem = (i: number, patch: Partial<ItemDraft>) => setItems((l) => l.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const pickClient = (id: number | null) => {
    setClientId(id);
    const c = clients.find((x) => x.id === id);
    if (c && !rName.trim()) setRName(c.name);
  };

  const save = async () => {
    if (!name.trim() || !rName.trim() || !subject.trim()) return setError('Indica nome, destinatario e oggetto');
    if (items.some((i) => !i.description.trim())) return setError('Ogni riga deve avere una descrizione');
    setBusy(true);
    const n = (v: string) => v.trim() || null;
    const body = {
      name: name.trim(),
      clientId,
      frequency: freq,
      nextDate: next,
      dueDays: Math.max(0, Number(dueDays) || 0),
      active: item?.active ?? true,
      data: {
        recipientName: rName.trim(),
        recipientVat: n(vat),
        recipientCf: n(cf),
        recipientAddress: n(address),
        recipientCap: n(cap),
        recipientCity: n(city),
        recipientProvince: n(prov),
        recipientEmail: n(email),
        recipientPec: n(pec),
        recipientSdi: sdi.trim() || '0000000',
        subject: subject.trim(),
        notes: n(notes),
        vatPct: num(vatPct),
        stampCents: stamp ? 200 : 0,
        paymentMethod: d?.paymentMethod ?? 'MP05',
        items: items.map((i) => ({ description: i.description.trim(), kind: i.kind, quantity: num(i.quantity), unitCents: parseEuro(i.unit) ?? 0 })),
      },
    };
    try {
      if (item) await api.put(`/api/recurring/${item.id}`, body);
      else await api.post('/api/recurring', body);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item || !(await ask(`Eliminare "${item.name}"? Le fatture già create restano.`))) return;
    try {
      await api.del(`/api/recurring/${item.id}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Modal
      title={item ? 'Modifica ricorrente' : 'Nuova fattura ricorrente'}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          {item && (
            <button type="button" className="btn ghost" onClick={() => void remove()} title="Elimina (le fatture già fatte restano)">
              <Icon name="trash" size={15} />
            </button>
          )}
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="fields-2">
        <Field label="Nome interno">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Canone assistenza Rossi" autoFocus={!item} />
        </Field>
        <Field label="Frequenza">
          <select value={freq} onChange={(e) => setFreq(e.target.value as Freq)}>
            {(Object.keys(FREQ) as Freq[]).map((f) => (
              <option key={f} value={f}>
                {FREQ[f]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Prossima fattura il">
          <DateInput value={next} onChange={setNext} />
        </Field>
        {clients.length > 0 && (
          <Field label="Cliente esistente" hint="Se lo colleghi, vedrà le fatture nella sua area.">
            <select value={clientId ?? ''} onChange={(e) => pickClient(e.target.value ? Number(e.target.value) : null)}>
              <option value="">— Non collegato —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Intestatario">
          <input value={rName} onChange={(e) => setRName(e.target.value)} placeholder="Denominazione in fattura" />
        </Field>
        <Field label="Oggetto" hint="Puoi usare {mese} e {anno}: diventano quelli del periodo (es. «Canone {mese} {anno}»).">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Field>
      </div>
      <div className="quote-edit-items">
        {items.map((it, idx) => (
          <div key={idx} className="quote-edit-row">
            <input className="qe-desc" value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} placeholder="Descrizione" />
            <select className="qe-kind" value={it.kind} onChange={(e) => setItem(idx, { kind: e.target.value as QuoteItemKind })}>
              <option value="fixed">A corpo</option>
              <option value="hours">Ore</option>
            </select>
            <input className="qe-num" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} inputMode="decimal" title="Quantità" />
            <input className="qe-num" value={it.unit} onChange={(e) => setItem(idx, { unit: e.target.value })} inputMode="decimal" placeholder="€" title="Prezzo unitario" />
            <button type="button" className="btn icon small ghost" disabled={items.length === 1} onClick={() => setItems((l) => l.filter((_, k) => k !== idx))} title="Togli la riga">
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
        <button type="button" className="btn small" onClick={() => setItems((l) => [...l, { description: '', kind: 'fixed', quantity: '1', unit: '' }])}>
          <Icon name="plus" size={14} /> Aggiungi riga
        </button>
      </div>
      <div style={{ height: 12 }} />
      <More label="Dati fiscali dell'intestatario" open={!!(vat || cf || address || cap || city || pec || (sdi && sdi !== '0000000'))}>
        <div className="fields-2">
          <Field label="P.IVA">
            <input value={vat} onChange={(e) => setVat(e.target.value)} />
          </Field>
          <Field label="Codice fiscale">
            <input value={cf} onChange={(e) => setCf(e.target.value)} />
          </Field>
          <Field label="Indirizzo">
            <input value={address} onChange={(e) => setAddress(e.target.value)} />
          </Field>
          <Field label="CAP">
            <input value={cap} onChange={(e) => setCap(e.target.value)} />
          </Field>
          <Field label="Città">
            <input value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <Field label="Provincia">
            <input value={prov} onChange={(e) => setProv(e.target.value.toUpperCase())} maxLength={2} />
          </Field>
          <Field label="Email">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="PEC">
            <input value={pec} onChange={(e) => setPec(e.target.value)} />
          </Field>
          <Field label="Codice destinatario SdI">
            <input value={sdi} onChange={(e) => setSdi(e.target.value.toUpperCase())} maxLength={7} />
          </Field>
        </div>
      </More>
      <More label="Scadenza, IVA, bollo e note" open={!!(dueDays !== '30' || vatPct !== '22' || stamp || notes)}>
        <div className="fields-3">
          <Field label="Scadenza pagamento (giorni)">
            <input type="number" min={0} value={dueDays} onChange={(e) => setDueDays(e.target.value)} />
          </Field>
          <Field label="IVA (%)" hint="0 in regime forfettario">
            <input value={vatPct} onChange={(e) => setVatPct(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Bollo">
            <label className="ag-allday">
              <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} /> Bollo virtuale 2 €
            </label>
          </Field>
        </div>
        <Field label="Note in fattura (facoltative)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </Field>
      </More>
    </Modal>
  );
}
